/**
 * chartTheme — resolves the --chart-* CSS tokens (tokens.css) into concrete
 * color values for chart.js and recharts, which can't consume var() references
 * directly (canvas rendering / SVG fill props).
 *
 * Resolution MUST go through getComputedStyle: --chart-1/--chart-2 are var()
 * references to brand tokens that themeEngine sets inline on <html>.
 *
 * Charts re-color on light/dark toggle via useThemeVersion(): include the
 * version in deps/keys so the component re-renders and re-resolves tokens
 * after every window "themechange" event.
 */
import { useEffect, useState } from "react";

const CHART_TOKEN_COUNT = 8;

/**
 * Resolve a single CSS custom property to its concrete value, for canvas/SVG
 * consumers that can't use var() references (chart.js datasets, SVG fills).
 */
export function getToken(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

const resolveToken = getToken;

/**
 * Categorical palette resolved from --chart-1..--chart-8.
 * Cycles when more than 8 series are requested.
 */
export function getChartPalette(n: number = CHART_TOKEN_COUNT): string[] {
  const base: string[] = [];
  for (let i = 1; i <= CHART_TOKEN_COUNT; i++) {
    const v = resolveToken(`--chart-${i}`);
    if (v) base.push(v);
  }
  if (base.length === 0) return [];
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(base[i % base.length]);
  return out;
}

export interface ChartTheme {
  grid: string;
  axisText: string;
  tooltipBg: string;
  tooltipText: string;
}

/** Structural chart colors (gridlines, axis labels, tooltip surface). */
export function getChartTheme(): ChartTheme {
  return {
    grid: resolveToken("--chart-grid"),
    axisText: resolveToken("--chart-axis-text"),
    tooltipBg: resolveToken("--surface-inverse"),
    tooltipText: resolveToken("--text-inverse"),
  };
}

/**
 * Increments whenever the theme or mode changes (window "themechange").
 * Use it in deps/keys of chart-rendering components so a dark-mode toggle
 * re-resolves the palette.
 */
export function useThemeVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const onThemeChange = () => setVersion((v) => v + 1);
    window.addEventListener("themechange", onThemeChange);
    return () => window.removeEventListener("themechange", onThemeChange);
  }, []);
  return version;
}

export interface StatusColors {
  success: string;
  warning: string;
  danger: string;
  info: string;
}

/**
 * Semantic status colors resolved from --status-* tokens, for chart marks
 * that are semantically fixed (pass-green, fail-red) rather than categorical.
 * Needed because SVG/canvas fill attributes can't consume var() references.
 */
export function getStatusColors(): StatusColors {
  return {
    success: resolveToken("--status-success"),
    warning: resolveToken("--status-warning"),
    danger: resolveToken("--status-danger"),
    info: resolveToken("--status-info"),
  };
}

/**
 * Applies token-resolved defaults to chart.js (global text + border colors).
 * Call at chart setup and again on every "themechange".
 */
export function applyChartJsTheme(chart: typeof import("chart.js").Chart) {
  const theme = getChartTheme();
  if (theme.axisText) chart.defaults.color = theme.axisText;
  if (theme.grid) chart.defaults.borderColor = theme.grid;
}
